import { Button, Descriptions, Input, Select } from 'antd'
import { useCreateTicket } from 'features/libra/ticket/createLibraTicket'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoUnit, RolodexEntryType } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
`

const ROLODEX_ENTRY_LIMIT = 100000

const labelStyle: CSSProperties = { width: '20%' }
const contentStyle: CSSProperties = { width: '80%' }
const descriptionStyles = {
  content: contentStyle,
  label: labelStyle,
}

function createDefaultTicketTitle(unit: GeckoUnit, workflowName: string) {
  return `${workflowName} - ${unit.data.name}`
}

export const RawCreateTicketDialog: FC<{
  organizationId: string
  afterCreate?: (createdTicketId: string) => void | Promise<void>
}> = ({ organizationId, afterCreate }) => {
  const { sites, units, isLoading } = useRolodexEntries(
    useMemo(
      () => ({
        type: [RolodexEntryType.UNIT, RolodexEntryType.SITE],
        tags: { organization: organizationId },
        limit: ROLODEX_ENTRY_LIMIT,
      }),
      [organizationId]
    )
  )

  const siteOptions = useMemo(() => {
    return sites.map((site) => ({
      label: site.data.name || site.id,
      value: site.id,
    }))
  }, [sites])

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  const unitOptions = useMemo(() => {
    return units
      .filter((unit) => unit.tags.site === selectedSiteId)
      .map((unit) => ({
        label: unit.data.name || unit.id,
        value: unit.id,
      }))
  }, [selectedSiteId, units])

  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null)
  const selectedUnit = useMemo(
    () => units.find((unit) => unit.id === selectedUnitId),
    [units, selectedUnitId]
  )

  const workflow = useWorkflow()
  const workflowName = workflow.name

  const [title, setTitle] = useState<string>(
    selectedUnit ? createDefaultTicketTitle(selectedUnit, workflowName) : ''
  )
  useEffect(() => {
    setTitle(
      selectedUnit ? createDefaultTicketTitle(selectedUnit, workflowName) : ''
    )
  }, [selectedUnit, workflowName])

  const createTicket = useCreateTicket()
  const canCreateTicket = Boolean(
    selectedSiteId && selectedUnitId && title.trim() !== ''
  )

  const { errorNotification, successNotification } = useGeckoNotifications()
  const [isCreatingTicket, setIsCreatingTicket] = useState(false)
  const handleCreateTicket = useCallback(async () => {
    if (!canCreateTicket) {
      return
    }
    const siteId = selectedSiteId
    const unitId = selectedUnitId
    if (!siteId || !unitId) {
      return
    }
    try {
      setIsCreatingTicket(true)
      const createdTicketId = await createTicket({
        title,
        organization: organizationId,
        site: siteId,
        unit: unitId,
      })
      successNotification('Ticket created successfully')
      setSelectedSiteId(null)
      setSelectedUnitId(null)
      setTitle('')
      if (afterCreate) {
        await afterCreate(createdTicketId)
      }
    } catch (error) {
      console.error(error)
      errorNotification(`Failed to create ticket: ${error}`)
    } finally {
      setIsCreatingTicket(false)
    }
  }, [
    canCreateTicket,
    createTicket,
    title,
    organizationId,
    selectedSiteId,
    selectedUnitId,
    successNotification,
    afterCreate,
    errorNotification,
  ])

  return (
    <Container>
      <Descriptions column={1} bordered styles={descriptionStyles}>
        <Descriptions.Item label='Site'>
          <Select
            loading={isLoading}
            showSearch
            optionFilterProp='label'
            options={siteOptions}
            placeholder='Select a site'
            value={selectedSiteId}
            onChange={setSelectedSiteId}
            disabled={isCreatingTicket}
          />
        </Descriptions.Item>
        <Descriptions.Item label='Unit'>
          <Select
            loading={isLoading}
            showSearch
            optionFilterProp='label'
            options={unitOptions}
            placeholder='Select a unit'
            value={selectedUnitId}
            onChange={setSelectedUnitId}
            disabled={isCreatingTicket}
          />
        </Descriptions.Item>
        <Descriptions.Item label='Title'>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isCreatingTicket}
          />
        </Descriptions.Item>
      </Descriptions>
      <Button
        type='primary'
        onClick={handleCreateTicket}
        disabled={!canCreateTicket || isCreatingTicket}
        loading={isCreatingTicket}>
        Create Ticket
      </Button>
    </Container>
  )
}

export const CreateTicketDialog = React.memo(RawCreateTicketDialog)
